import { RedirectToSignIn, SignIn, useAuth } from "@clerk/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import {
  Link,
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useLocation,
} from "@tanstack/react-router";
import { useEffect, type ReactElement } from "react";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { NotProvisioned } from "@/components/not-provisioned";
import { NotFound } from "@/components/not-found";
import { BreadcrumbSetter, type BreadcrumbSegment } from "./components/v2/breadcrumb";
import { ShellHeader, type ShellLookSwitch } from "./components/v2/shell-header";
import type { AlwaysAiUser } from "../../shared/api-types";
import { RdsWorkspace } from "./routes/rds";
import { UploadWorkspace } from "./routes/upload";
import { RdDetail } from "./routes/rd-detail";
import { SessionDetail } from "./routes/session-detail";
import {
  MeetingsSurface,
  ObservationsSurface,
  RdWorkspaceLayout,
  ScorecardsSurface,
} from "./routes/rd-workspace";
import { MetricsSurface } from "./routes/metrics-surface";
import { ChatThreadPage, ChatsNewPage } from "./routes/chats-pages";
import { FormSnapshotPage, FormsSurfacePage } from "./routes/forms-pages";
import { AdminRds } from "./routes/admin/rds";
import { AdminCoverage } from "./routes/admin/coverage";
import { AdminUsage } from "./routes/admin/usage";
import { AdminMigrate } from "./routes/admin/migrate";
import { AdminDev, AdminDevDetail } from "./routes/admin/dev";
import { AdminStarterPrompts } from "./routes/admin/starter-prompts";
import { AdminScorecardWorkbench } from "./routes/admin/scorecard-workbench";
import { AdminSoapWorkbench } from "./routes/admin/soap-workbench";
import { canReadAppointments, landingPathForRole } from "./lib/permissions";
import { ALWAYS_BRAND_FLAG, RD_HOME_FLAG, useFeatures } from "./lib/use-features";
import { useAlwaysThemeSync } from "./lib/use-always-theme";
import {
  lastLookUpdate,
  lookSwitchTarget,
  resolveLook,
  useShellLook,
  type ShellLook,
} from "./lib/look-preference";
import { validateMessagesSearch } from "./lib/messages-client";
import { hasRailSections } from "./lib/rail-sections";
import { chooseWorkspaceFrame, type WorkspaceFrame } from "./lib/workspace-frame";
import { AppShell } from "./components/v2/app-shell";
import { AlwaysShell } from "./components/v2/always-shell";
import { validateAppointmentListSearch } from "./lib/appointments";
import { AppointmentDetailPage, AppointmentsPage } from "./routes/appointments";
import { SessionDraftPage } from "./routes/session-draft";
import { MessageThreadPage, MessagesPage } from "./routes/messages";
import { NotificationsPage } from "./routes/notifications";
import { NotificationSettingsPage } from "./routes/notification-settings";
import { PatientPage, PatientsPage } from "./routes/patients";

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => <Navigate to="/rds" />,
});
// Clerk's path-based sign-in renders sub-routes (factor-one, sso-callback, …)
// under /sign-in/*, so the page needs both the exact and the catch-all match.
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignInPage,
});
const signInCatchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in/$",
  component: SignInPage,
});
// Pathless layout mirroring legacy src/app/(workspace)/layout.tsx: signed-out
// redirect, provisioning gate, 65px header with wordmark + breadcrumbs.
const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "workspace",
  component: WorkspaceShell,
});
// Pathless layout mirroring legacy src/app/admin/layout.tsx: signed-out
// redirect only — admin pages render their own full-page chrome.
const adminShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "admin-shell",
  component: AdminShell,
});
const adminIndexRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin",
  component: () => <Navigate to="/admin/rds" />,
});
const rdsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/rds",
  component: RdsWorkspace,
});
const uploadRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/transcripts/upload",
  component: UploadWorkspace,
});
const appointmentsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/appointments",
  validateSearch: validateAppointmentListSearch,
  component: AppointmentsPage,
});
const appointmentDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/appointments/$appointmentId",
  validateSearch: validateAppointmentListSearch,
  component: AppointmentDetailPage,
});
// NO-830: the RD's in-app notification list, in the single-rail frame only.
const notificationsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/notifications",
  component: NotificationsPage,
});
// NO-925: the RD's Messages list and one Client's threads, in the single-rail
// frame only. `?rdId=` is an admin's picked RD, validated like the Appointments
// page's.
const messagesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/messages",
  validateSearch: validateMessagesSearch,
  component: MessagesPage,
});
const messageThreadRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/messages/$patientId",
  validateSearch: validateMessagesSearch,
  component: MessageThreadPage,
});
// NO-834: the viewer's notification settings, from the account menu, in the
// single-rail frame only.
const notificationSettingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings/notifications",
  component: NotificationSettingsPage,
});
// NO-835: the RD's Patients list and the Client page under it (a placeholder
// the patient page ticket replaces), in the single-rail frame only.
const patientsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/patients",
  component: PatientsPage,
});
const patientRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/patients/$patientId",
  component: PatientPage,
});
const sessionDraftRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/sessions/$sessionId/draft",
  component: SessionDraftPage,
});
const sessionDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/rds/$rdId/sessions/$sessionId",
  component: SessionDetail,
});
// rd-workspace per-surface shell: a pathless layout (RdWorkspaceSidebar +
// Outlet) wrapping /rds/$rdId and the surface routes. /rds/$rdId is
// the RD Snapshot when the supervisor-metrics flag is on; otherwise it
// redirects to /scorecards (NO-558).
const rdWorkspaceShellRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  id: "rd-workspace-shell",
  component: RdWorkspaceLayout,
});
const rdDetailRoute = createRoute({
  getParentRoute: () => rdWorkspaceShellRoute,
  path: "/rds/$rdId",
  component: RdDetail,
});
const scorecardsRoute = createRoute({
  getParentRoute: () => rdWorkspaceShellRoute,
  path: "/rds/$rdId/scorecards",
  component: ScorecardsSurface,
});
const metricsSurfaceRoute = createRoute({
  getParentRoute: () => rdWorkspaceShellRoute,
  path: "/rds/$rdId/metrics",
  component: MetricsSurface,
});
const observationsSurfaceRoute = createRoute({
  getParentRoute: () => rdWorkspaceShellRoute,
  path: "/rds/$rdId/observations",
  component: ObservationsSurface,
});
const meetingsSurfaceRoute = createRoute({
  getParentRoute: () => rdWorkspaceShellRoute,
  path: "/rds/$rdId/meetings",
  validateSearch: (search: Record<string, unknown>): { meetingNoteId?: string } => ({
    meetingNoteId:
      typeof search.meetingNoteId === "string" ? search.meetingNoteId : undefined,
  }),
  component: MeetingsSurface,
});
const formsSurfaceRoute = createRoute({
  getParentRoute: () => rdWorkspaceShellRoute,
  path: "/rds/$rdId/forms",
  // `?profile=setup` is the Snapshot's link to set up a missing profile (NO-669).
  validateSearch: (
    search: Record<string, unknown>
  ): { edit?: string; profile?: "setup" } => ({
    edit: typeof search.edit === "string" ? search.edit : undefined,
    profile: search.profile === "setup" ? "setup" : undefined,
  }),
  component: FormsSurfacePage,
});
const formSnapshotRoute = createRoute({
  getParentRoute: () => rdWorkspaceShellRoute,
  path: "/rds/$rdId/forms/$formId",
  component: FormSnapshotPage,
});
const chatsNewRoute = createRoute({
  getParentRoute: () => rdWorkspaceShellRoute,
  path: "/rds/$rdId/chats/new",
  validateSearch: (
    search: Record<string, unknown>
  ): { sourceType?: string; sourceId?: string } => {
    if (typeof search.sourceType === "string" && typeof search.sourceId === "string") {
      return { sourceType: search.sourceType, sourceId: search.sourceId };
    }

    // Legacy deep links predate the typed pair: ?formSource=<formId> (form
    // flyover "Ask about this") and ?source=<sessionId> (session flyover).
    if (typeof search.formSource === "string" && search.formSource) {
      return { sourceType: "form", sourceId: search.formSource };
    }

    if (typeof search.source === "string" && search.source) {
      return { sourceType: "session", sourceId: search.source };
    }

    return {};
  },
  component: ChatsNewPage,
});
const chatThreadRoute = createRoute({
  getParentRoute: () => rdWorkspaceShellRoute,
  path: "/rds/$rdId/chats/$chatId",
  component: ChatThreadPage,
});
const adminRdsRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin/rds",
  component: AdminRds,
});
const adminCoverageRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin/coverage",
  component: AdminCoverage,
});
const adminUsageRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin/usage",
  component: AdminUsage,
});
const adminStarterPromptsRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin/starter-prompts",
  component: AdminStarterPrompts,
});
const adminMigrateRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin/migrate",
  component: AdminMigrate,
});
// soap-workbench-boundary.test.ts greps this route declaration on one line.
// prettier-ignore
const adminSoapWorkbenchRoute = createRoute({ getParentRoute: () => adminShellRoute, path: "/admin/soap-workbench", component: AdminSoapWorkbench });
const adminScorecardWorkbenchRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin/scorecard-workbench",
  component: AdminScorecardWorkbench,
});
const adminDevRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin/dev",
  component: AdminDev,
});
const adminDevDetailRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "/admin/dev/$id",
  component: AdminDevDetail,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  signInCatchAllRoute,
  workspaceRoute.addChildren([
    rdsRoute,
    uploadRoute,
    appointmentsRoute,
    appointmentDetailRoute,
    notificationsRoute,
    messagesRoute,
    messageThreadRoute,
    notificationSettingsRoute,
    patientsRoute,
    patientRoute,
    sessionDraftRoute,
    sessionDetailRoute,
    rdWorkspaceShellRoute.addChildren([
      rdDetailRoute,
      scorecardsRoute,
      metricsSurfaceRoute,
      observationsSurfaceRoute,
      meetingsSurfaceRoute,
      formsSurfaceRoute,
      formSnapshotRoute,
      chatsNewRoute,
      chatThreadRoute,
    ]),
  ]),
  adminShellRoute.addChildren([
    adminIndexRoute,
    adminRdsRoute,
    adminCoverageRoute,
    adminUsageRoute,
    adminStarterPromptsRoute,
    adminMigrateRoute,
    adminSoapWorkbenchRoute,
    adminScorecardWorkbenchRoute,
    adminDevRoute,
    adminDevDetailRoute,
  ]),
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// The root owns the theme attribute on <html> (NO-790), so every route, the
// 404 and anything portaled into <body> share one look.
function RootLayout(): ReactElement {
  useAlwaysThemeSync();

  return <Outlet />;
}

function SignInPage(): ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <SignIn
        routing="path"
        path={import.meta.env.VITE_CLERK_SIGN_IN_URL ?? "/sign-in"}
        fallbackRedirectUrl="/rds"
      />
    </div>
  );
}

function AdminShell(): ReactElement | null {
  const { isLoaded, isSignedIn } = useAuth({ treatPendingAsSignedOut: false });
  if (!isLoaded) return null;

  if (!isSignedIn) return <RedirectToSignIn />;

  return <Outlet />;
}

function isNotProvisionedError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 403;
}

function LegacyChrome({
  user,
  rdHomeEnabled,
  pathname,
  settled,
  lookSwitch,
}: {
  user: AlwaysAiUser | undefined;
  rdHomeEnabled: boolean;
  pathname: string;
  settled: boolean;
  lookSwitch?: ShellLookSwitch;
}): ReactElement {
  const landingPath = user ? landingPathForRole(user, rdHomeEnabled) : "/rds";
  // The legacy chrome links to nothing, so a viewer with Appointment reach
  // (an admin, or a supervisor who is also an RD) needs a way in that is
  // not a typed URL. Rides the same dark launch as the surface itself.
  const canSeeAppointments = user !== undefined && canReadAppointments(user);
  const showAppointmentsNav = canSeeAppointments && rdHomeEnabled;

  return (
    <div className="h-screen flex flex-col bg-background">
      <ShellHeader
        landingPath={landingPath}
        lookSwitch={lookSwitch}
        nav={
          showAppointmentsNav ? (
            <Link
              to="/appointments"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Appointments
            </Link>
          ) : undefined
        }
      />
      <main className="flex-1 min-h-0">
        {/* Route-derived fallback crumbs. Rendered before the Outlet so page-level
            BreadcrumbSetter effects run afterwards and win with real labels. */}
        <BreadcrumbSetter segments={routeFallbackSegments(pathname)} />
        {settled ? <Outlet /> : null}
      </main>
    </div>
  );
}

interface WorkspaceViewer {
  isLoaded: boolean;
  isSignedIn: boolean | undefined;
  me: UseQueryResult<{ user: AlwaysAiUser }>;
  pathname: string;
  mePending: boolean;
  rdHomeEnabled: boolean;
  frame: WorkspaceFrame;
  lookSwitch: ShellLookSwitch | undefined;
}

// Who is looking, what the flags say, and which look they chose, resolved into
// one frame decision (lib/workspace-frame.ts).
function useWorkspaceViewer(): WorkspaceViewer {
  const { getToken, isLoaded, isSignedIn, userId } = useAuth({
    treatPendingAsSignedOut: false,
  });
  const location = useLocation();
  const me = useQuery({
    queryKey: ["current-user"],
    enabled: isLoaded && isSignedIn,
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
  const features = useFeatures();
  const shellLook = useShellLook(userId);
  const mePending = !me.isSuccess && !me.isError;
  const featuresPending = !features.isSuccess && !features.isError;
  // A failed flag evaluation reads as off: RD Home stays dark.
  const rdHomeEnabled = features.data?.data[RD_HOME_FLAG] === true;
  const flagOn = features.data?.data[ALWAYS_BRAND_FLAG] === true;
  const look = resolveLook({ flagOn, stored: shellLook.stored });
  const frame = chooseWorkspaceFrame({
    pathname: location.pathname,
    user: me.data?.user,
    mePending,
    featuresPending,
    rdHomeEnabled,
    look,
    lastLook: shellLook.lastLook,
  });
  const update = lastLookUpdate({
    flagOn,
    featuresPending,
    mePending,
    frame: frame.kind,
  });
  useLastLookRecord(update, shellLook.recordLastLook);
  const user = me.data?.user;
  const railAvailable = user !== undefined && hasRailSections(user, rdHomeEnabled);
  const target = lookSwitchTarget({ flagOn, look, railAvailable });
  const lookSwitch = target
    ? { label: target.label, onSelect: () => shellLook.chooseLook(target.target) }
    : undefined;

  return {
    isLoaded,
    isSignedIn,
    me,
    pathname: location.pathname,
    mePending,
    rdHomeEnabled,
    frame,
    lookSwitch,
  };
}

function useLastLookRecord(
  update: { write: ShellLook | null } | null,
  recordLastLook: (look: ShellLook | null) => void
): void {
  const pending = update === null;
  const write = update?.write ?? null;
  useEffect(() => {
    if (!pending) recordLastLook(write);
  }, [pending, write, recordLastLook]);
}

function WorkspaceShell(): ReactElement | null {
  const viewer = useWorkspaceViewer();

  if (!viewer.isLoaded) return null;

  if (!viewer.isSignedIn) return <RedirectToSignIn />;

  if (viewer.me.isError && isNotProvisionedError(viewer.me.error)) {
    return <NotProvisioned />;
  }

  return renderWorkspaceFrame(viewer);
}

function renderWorkspaceFrame(viewer: WorkspaceViewer): ReactElement {
  const { frame, lookSwitch } = viewer;

  switch (frame.kind) {
    case "blank":
      return <div className="h-screen bg-background" />;
    case "redirect":
      return <Navigate to={frame.to} replace />;
    case "always":
      return (
        <AlwaysShell user={frame.user} lookSwitch={lookSwitch}>
          {/* Fallback crumbs first, so page-level setters win (as in LegacyChrome). */}
          <BreadcrumbSetter segments={routeFallbackSegments(viewer.pathname)} />
          <Outlet />
        </AlwaysShell>
      );
    case "rd-shell":
      return (
        <AppShell user={frame.user} lookSwitch={lookSwitch}>
          <Outlet />
        </AppShell>
      );
    case "legacy":
      return (
        <LegacyChrome
          user={viewer.me.data?.user}
          rdHomeEnabled={viewer.rdHomeEnabled}
          pathname={viewer.pathname}
          settled={!viewer.mePending}
          lookSwitch={lookSwitch}
        />
      );
  }
}

// Appointment routes carry no fallback crumbs: they are RD-only, and the RD
// shell owns no breadcrumbs — wayfinding lives in the canvas there.
function routeFallbackSegments(pathname: string): BreadcrumbSegment[] {
  if (pathname === "/rds") return [{ label: "RDs" }];

  if (pathname === "/transcripts/upload") return [{ label: "Transcript upload" }];

  // rd-workspace per-surface routes: [RDs → RD → surface label]. Page-level
  // BreadcrumbSetter effects override these with real labels once loaded.
  const surfaceMatch = pathname.match(
    /^\/rds\/([^/]+)\/(scorecards|metrics|observations|meetings|forms|chats)(?:\/([^/]+))?$/
  );

  if (surfaceMatch) {
    const [, rdSeg, surface, sub] = surfaceMatch;
    const rdCrumb = { label: "RD", href: `/rds/${rdSeg}` };
    const SURFACE_LABELS: Record<string, string> = {
      scorecards: "Session Scorecards",
      metrics: "Metrics",
      observations: "Observations",
      meetings: "Meetings",
      forms: "Functional Forms",
      chats: "Chat",
    };
    const base: BreadcrumbSegment[] = [
      { label: "RDs", href: "/rds" },
      rdCrumb,
      { label: SURFACE_LABELS[surface] },
    ];

    if (surface === "forms" && sub) {
      base.push({ label: "Form" });
    } else if (surface === "chats" && sub === "new") {
      base[base.length - 1] = { label: "New chat" };
    }

    return base;
  }

  const match = pathname.match(/^\/rds\/([^/]+)(?:\/sessions\/([^/]+))?$/);

  if (match) {
    return match[2]
      ? [
          { label: "RDs", href: "/rds" },
          { label: "RD", href: `/rds/${match[1]}` },
          { label: "Session" },
        ]
      : [{ label: "RDs", href: "/rds" }, { label: "RD" }];
  }

  return [];
}
